/* eslint-disable react/display-name */
import { Suspense, lazy, useEffect } from "react";
import { Navigate, useRoutes } from "react-router-dom";

import MutateLoader from "../components/MutateLoader";
import { DASHBOARD_PATH, StorageConstants } from "../constants/AppConstants";
import AuthGuard from "../guards/AuthGuard";
import GuestGuard from "../guards/GuestGuard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
import ObjectStorage from "../modules/ObjectStorage";
import { get } from "lodash";
import hexToRgb from "../helper/hexToRgb";
import lightenRgb from "../helper/lightenRgb";

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  return (
    <Suspense fallback={<MutateLoader />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  useEffect(() => {
    const previousePrimaryColor = get(
      ObjectStorage.getItem(StorageConstants.PRIMARY_THEME_COLOR),
      "value",
      "#5a0a45"
    );
    const previouseSecondaryColor = get(
      ObjectStorage.getItem(StorageConstants.SECONDARY_THEME_COLOR),
      "value"
    );
    const rgb = previouseSecondaryColor && hexToRgb(previouseSecondaryColor);
    const lightColor = previouseSecondaryColor
      ? lightenRgb(rgb, 70)
      : "#F5F5F5";
    document.documentElement.style.setProperty(
      "--color-primary",
      previousePrimaryColor
    );
    document.documentElement.style.setProperty("--color-secondary", lightColor);
  }, []);
  return useRoutes([
    {
      path: "/",
      children: [
        { element: <Navigate to={"/poster/home"} replace />, index: true },
        {
          path: "/login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: `/${DASHBOARD_PATH.ROOT}`,
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: (
            <Navigate
              to={`/${DASHBOARD_PATH.ROOT}/${DASHBOARD_PATH.MENU}`}
              replace
            />
          ),
          index: true,
        },
        { path: DASHBOARD_PATH.MENU, element: <ProductMenu /> },
        { path: DASHBOARD_PATH.PAYMENT, element: <Payment /> },
        { path: DASHBOARD_PATH.PAYMENT_STATUS, element: <PaymentStatus /> },
        { path: DASHBOARD_PATH.MODE, element: <Mode /> },
        { path: DASHBOARD_PATH.COUNTER, element: <CounterScreen /> },
        { path: DASHBOARD_PATH.THANKYOU, element: <ThankYouScreen /> },
        { path: DASHBOARD_PATH.SCAN_QR_PRINT, element: <ScanQRprintBill /> },
      ],
    },
    {
      path: "/poster",
      element: (
        <AuthGuard>
          <LogoOnlyLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/poster/home" replace />, index: true },
        { path: "home", element: <Poster /> },
      ],
    },
    { path: "*", element: <Navigate to="/poster/home" replace /> },
  ]);
}

const Login = Loadable(lazy(() => import("../pages/Auth/Login")));
const DashboardLayout = Loadable(
  lazy(() => import("../layouts/DashboardLayout"))
);
const ProductMenu = Loadable(lazy(() => import("../pages/App/ProductMenu")));
const Payment = Loadable(lazy(() => import("../pages/App/Payment")));
const PaymentStatus = Loadable(
  lazy(() => import("../pages/App/PaymentStatus"))
);
const CounterScreen = Loadable(
  lazy(() => import("../pages/App/CounterScreen"))
);
const Mode = Loadable(lazy(() => import("../pages/App/Mode")));
const ThankYouScreen = Loadable(
  lazy(() => import("../pages/App/ThankYouScreen"))
);
const Poster = Loadable(lazy(() => import("../pages/App/Poster")));
const ScanQRprintBill = Loadable(
  lazy(() => import("../pages/App/ScanqrPrintbill"))
);
// const Page404 = Loadable(lazy(() => import("../pages/Page404")));
